import { Injectable } from "@angular/core";
import { AlulaDevice } from "@app/project/model/alula-device";
import { AlulaUser } from "@app/project/model/alula-user";
import { AlulaDatabaseService, AlulaResponseData } from "@app/shared/databases/alula.database";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { DataServiceError } from "../../shared/services/data-service-error";
import { AlulaDealer } from "../model/alula-dealer";

@Injectable({
    providedIn: "root",
})
export class AlulaUserService {
    userEndpoint = "users";

    createAlulaUser(user: AlulaUser, device: AlulaDevice, dealer?: AlulaDealer): Observable<AlulaUser> {
        return this.alulaDatabaseService
            .createAlulaUser$({
                nameFirst: user.firstName,
                nameLast: user.lastName,
                dealerId: user.dealerId || dealer.id,
                email: user.email,
                uType: 32,
                mac_address: device.macAddress,
            })
            .pipe(
                map(_user => AlulaUser.fromDbObject(_user)),
                catchError(DataServiceError.handleError())
            );
    }

    updateAlulaUser(user: AlulaUser, device: AlulaDevice): Observable<AlulaUser> {
        return this.alulaDatabaseService
            .updateRecord$(
                this.userEndpoint,
                {
                    nameFirst: user.firstName,
                    nameLast: user.lastName,
                    email: user.email,
                },
                user.id
            )
            .pipe(
                map(() => user),
                catchError(DataServiceError.handleError())
            );
    }

    getUserByPartialUsername(macAddress: string): Observable<AlulaUser> {
        const mac = macAddress?.replace(/:/g, "");
        const filterString = `filter[username][$like]=${mac}-%&sort=-dateEntered`;

        return this.alulaDatabaseService.getFilteredRecords$(this.userEndpoint, filterString).pipe(
            map((result: AlulaResponseData[] = []) => AlulaUser.fromDbObject(result[0])),
            catchError(DataServiceError.handleError())
        );
    }

    getUserByUTypeAndEmail(payload: { userType: number; email: string }): Observable<AlulaUser> {
        return this.alulaDatabaseService
            .getRecords$(this.userEndpoint, {
                uType: payload.userType,
                email: payload.email,
            })
            .pipe(
                map((result: AlulaResponseData[] = []) => AlulaUser.fromDbObject(result[0])),
                catchError(DataServiceError.handleError())
            );
    }

    upsertAlulaUser(payload: { user: AlulaUser; device: AlulaDevice; dealer?: AlulaDealer }) {
        const userId = payload.user.id;
        const deviceUserId = payload.device.customerId;

        if (!userId && !deviceUserId) {
            // create the customer if it doesn't exist and the panel is not assigned
            return this.createAlulaUser(payload.user, payload.device, payload.dealer);
        }
        if (!deviceUserId || userId === deviceUserId) {
            // update the user if the device isn't assigned or it matches the customer on the device
            return this.updateAlulaUser(payload.user, payload.device);
        }
        return DataServiceError.handleError(payload)({
            message: "There was a problem updating the security device",
        });
    }

    constructor(private alulaDatabaseService: AlulaDatabaseService) {}
}
